import styled from '@emotion/styled';
import React from 'react';

import type { LensDivProps, ResponsiveType } from '../../types';
import {
  getColorValue,
  getRadius,
  getShadow,
  getSize,
  getSizeValue,
} from '../../utilities';

const getBorderSideAndColor = (side, color, width) => {
  const borderColor = color ? color : 'border';
  const borderWidth = getSizeValue(width);
  const borderStyle = `${borderWidth} solid ${getColorValue(borderColor)}`;

  if (side) {
    if (side === 'all') {
      return `border: ${borderStyle};`;
    }

    return `border-${side}: ${borderStyle};`;
  }
};

const ContainerWrapper = styled.div<ContainerWrapperProps>`
  ${props => props.position && `position: ${props.position}`};
  ${props => props.overflow && `overflow: ${props.overflow}`};
  ${props =>
    props.backgroundColor &&
    `background-color: ${getColorValue(props.backgroundColor)}`};
  ${props =>
    props.backgroundImage && `background-image: ${props.backgroundImage}`}
  ${props =>
    props.contentColor && `color: ${getColorValue(props.contentColor)}`};
  ${props =>
    getBorderSideAndColor(
      props.borderSide,
      props.borderColor,
      props.borderWidth
    )};
  ${props => getRadius(props.radius)};
  ${props => getShadow(props.shadow)};
  ${props => getSize('width', props.width)};
  ${props => getSize('height', props.height)};
  ${props => getSize('min-width', props.minWidth)};
  ${props => getSize('min-height', props.minHeight)};
  ${props => getSize('max-width', props.maxWidth)};
  ${props => getSize('max-height', props.maxHeight)};
  ${props => getSize('padding', props.padding)};
  ${props => getSize('padding-top', props.paddingTop)};
  ${props => getSize('padding-right', props.paddingRight)};
  ${props => getSize('padding-bottom', props.paddingBottom)};
  ${props => getSize('padding-left', props.paddingLeft)};
  ${props => getSize('margin', props.margin)};
  ${props => getSize('margin-top', props.marginTop)};
  ${props => getSize('margin-right', props.marginRight)};
  ${props => getSize('margin-bottom', props.marginBottom)};
  ${props => getSize('margin-left', props.marginLeft)};
  ${props => getSize('top', props.top)};
  ${props => getSize('right', props.right)};
  ${props => getSize('bottom', props.bottom)};
  ${props => getSize('left', props.left)};
  ${props => props.zIndex && `z-index: ${props.zIndex}`};
`;

const Container = ({
  children,
  backgroundColor,
  backgroundImage,
  contentColor,
  borderColor,
  radius,
  borderSide,
  borderWidth = '1px',
  shadow,
  padding,
  paddingX,
  paddingY,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  margin,
  marginX,
  marginY,
  marginLeft,
  marginRight,
  marginTop,
  marginBottom,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  htmlTag = 'div',
  position,
  overflow,
  zIndex,
  top,
  bottom,
  left,
  right,
  refHandler,
  ...props
}: ContainerProps &
  React.ComponentProps<typeof ContainerWrapper> &
  Omit<
    LensDivProps,
    keyof ContainerProps & React.ComponentProps<typeof ContainerWrapper>
  >) => {
  return (
    <ContainerWrapper
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
      contentColor={contentColor}
      borderColor={borderColor}
      radius={radius}
      borderSide={borderSide}
      shadow={shadow}
      padding={padding}
      paddingLeft={paddingX || paddingLeft}
      paddingRight={paddingX || paddingRight}
      paddingTop={paddingY || paddingTop}
      paddingBottom={paddingY || paddingBottom}
      margin={margin}
      marginLeft={marginX || marginLeft}
      marginRight={marginX || marginRight}
      marginTop={marginY || marginTop}
      marginBottom={marginY || marginBottom}
      width={width}
      height={height}
      minWidth={minWidth}
      minHeight={minHeight}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      as={htmlTag}
      position={position}
      top={top}
      bottom={bottom}
      left={left}
      right={right}
      overflow={overflow}
      zIndex={zIndex}
      borderWidth={borderWidth}
      ref={ref => refHandler && refHandler(ref)}
      {...props}
    >
      {children}
    </ContainerWrapper>
  );
};

export const availableBorderSides = ['all', 'left', 'right', 'top', 'bottom'];
export const availableRadii = [
  'none',
  '50',
  '100',
  '150',
  '175',
  '200',
  '250',
  '300',
  'round',
];
export const availableHtmlTags = [
  'div',
  'header',
  'article',
  'section',
  'nav',
  'aside',
  'footer',
  'main',
  'span',
  'form',
];

interface SharedProps {
  backgroundColor?: string;
  backgroundImage?: string;
  contentColor?: string;
  borderColor?: string;
  radius?:
    | 'medium'
    | 'large'
    | 'xlarge'
    | 'full'
    | 'none'
    | '50'
    | '100'
    | '150'
    | '175'
    | '200'
    | '250'
    | '300'
    | 'round';
  borderSide?: 'all' | 'left' | 'right' | 'top' | 'bottom';
  shadow?: 'small' | 'medium' | 'large';
  padding?: ResponsiveType<string | number> | null;
  paddingX?: ResponsiveType<string | number> | null;
  paddingY?: ResponsiveType<string | number> | null;
  paddingLeft?: ResponsiveType<string | number> | null;
  paddingRight?: ResponsiveType<string | number> | null;
  paddingTop?: ResponsiveType<string | number> | null;
  paddingBottom?: ResponsiveType<string | number> | null;
  margin?: ResponsiveType<string | number> | null;
  marginX?: ResponsiveType<string | number> | null;
  marginY?: ResponsiveType<string | number> | null;
  marginLeft?: ResponsiveType<string | number> | null;
  marginRight?: ResponsiveType<string | number> | null;
  marginTop?: ResponsiveType<string | number> | null;
  marginBottom?: ResponsiveType<string | number> | null;
  width?: ResponsiveType<string | number>;
  height?: ResponsiveType<string | number>;
  minWidth?: ResponsiveType<string | number>;
  minHeight?: ResponsiveType<string | number>;
  maxWidth?: ResponsiveType<string | number>;
  maxHeight?: ResponsiveType<string | number>;
  children?: React.ReactNode;
  position?: string;
  overflow?: string;
  top?: ResponsiveType<string | number> | null;
  bottom?: ResponsiveType<string | number> | null;
  left?: ResponsiveType<string | number> | null;
  right?: ResponsiveType<string | number> | null;
  zIndex?: number | string;
  borderWidth?: string | number;
  // NOTE: Covers the most common types since the user can change the `as` value using `htmlTag`
  refHandler?:
    | ((ref: HTMLElement) => void)
    | ((ref: HTMLDivElement) => void)
    | ((ref: HTMLSpanElement) => void);
}

interface ContainerProps extends SharedProps {
  htmlTag?:
    | 'div'
    | 'header'
    | 'article'
    | 'section'
    | 'nav'
    | 'aside'
    | 'footer'
    | 'main'
    | 'span'
    | 'form'
    | 'table';
}

interface ContainerWrapperProps extends SharedProps {
  as?: string;
}

export default Container;
